import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import io from 'socket.io-client';
import './WhatsAppWebPanel.css';

const WhatsAppWebPanel = ({ isOpen, onClose, onSelectContact }) => {
  const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3000';
  
  const [contacts, setContacts] = useState([]);
  const [selectedContact, setSelectedContact] = useState(null);
  const [messages, setMessages] = useState([]);
  const [status, setStatus] = useState({ isReady: false, hasQR: false, qrCode: null });
  const [qrCode, setQrCode] = useState(null);
  const [loading, setLoading] = useState(true);
  const socketRef = useRef(null);

  const loadStatus = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/whatsapp-web/status`);
      console.log('📊 Estado de WhatsApp Web recibido:', response.data);
      
      if (response.data.success) {
        const newStatus = {
          isReady: Boolean(response.data.isReady),
          hasQR: Boolean(response.data.hasQR),
          qrCode: response.data.qrCode || null
        };
        console.log('📊 Estado procesado:', newStatus);
        setStatus(newStatus);
        
        if (newStatus.hasQR && !qrCode) {
          loadQRCode();
        }
      } else {
        console.log('⚠️  API no devolvió success');
        // Si no hay éxito, establecer estado desconectado
        setStatus({ isReady: false, hasQR: false, qrCode: null });
      }
    } catch (error) {
      console.error('❌ Error cargando estado:', error);
      // En caso de error, establecer estado desconectado
      setStatus({ isReady: false, hasQR: false, qrCode: null });
    }
  };

  const loadQRCode = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/whatsapp-web/qr`);
      if (response.data.success && response.data.qrCode) {
        setQrCode(response.data.qrCode);
      }
    } catch (error) {
      console.error('Error cargando QR:', error);
    }
  };

  const loadContacts = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API_URL}/api/contacts`);
      const allContacts = response.data.contacts || [];
      console.log(`📋 Total de contactos: ${allContacts.length}`);
      
      // Filtrar solo contactos de WhatsApp Web
      const webContacts = allContacts.filter(c => c.source === 'whatsapp-web');
      console.log(`📱 Contactos de WhatsApp Web: ${webContacts.length}`);
      
      if (webContacts.length === 0) {
        console.log('⚠️  No hay contactos con source="whatsapp-web"');
        console.log('   Fuentes encontradas:', [...new Set(allContacts.map(c => c.source))]);
      }
      
      // Obtener último mensaje de cada contacto
      const contactsWithMessages = await Promise.all(
        webContacts.map(async (contact) => {
          try {
            const msgResponse = await axios.get(`${API_URL}/api/messages/${contact.phoneNumber}?source=whatsapp-web&limit=1`);
            const lastMessage = msgResponse.data.messages?.[0] || null;
            return {
              ...contact,
              lastMessage: lastMessage,
              lastMessageTime: lastMessage?.timestamp || lastMessage?.createdAt
            };
          } catch (error) {
            console.error(`   ⚠️  Error cargando mensajes para ${contact.phoneNumber}:`, error.message);
            return { ...contact, lastMessage: null };
          }
        })
      );

      // Ordenar por último mensaje
      contactsWithMessages.sort((a, b) => {
        const timeA = a.lastMessageTime ? new Date(a.lastMessageTime).getTime() : 0;
        const timeB = b.lastMessageTime ? new Date(b.lastMessageTime).getTime() : 0;
        return timeB - timeA;
      });

      console.log(`✅ Contactos cargados: ${contactsWithMessages.length}`);
      setContacts(contactsWithMessages);
      setLoading(false);
    } catch (error) {
      console.error('❌ Error cargando contactos:', error);
      setLoading(false);
    }
  };

  const loadMessages = async (phoneNumber) => {
    try {
      const response = await axios.get(`${API_URL}/api/messages/${phoneNumber}?source=whatsapp-web`);
      setMessages(response.data.messages || []);
    } catch (error) {
      console.error('Error cargando mensajes:', error);
    }
  };

  const handleContactClick = (contact) => {
    console.log('📞 Abriendo conversación con:', contact.phoneNumber);
    setSelectedContact(contact);
    loadMessages(contact.phoneNumber);
    if (onSelectContact) {
      onSelectContact(contact);
    }
  };

  // Conectar a Socket.io cuando se abre el panel
  useEffect(() => {
    if (isOpen) {
      // Conectar a Socket.io
      socketRef.current = io(API_URL);
      
      // Escuchar actualizaciones de estado
      socketRef.current.on('whatsapp-web-status', (newStatus) => {
        console.log('📡 Estado recibido por Socket.io:', newStatus);
        setStatus({
          isReady: Boolean(newStatus.isReady),
          hasQR: Boolean(newStatus.hasQR),
          qrCode: newStatus.qrCode || null
        });
      });
      
      // Escuchar QR Code
      socketRef.current.on('whatsapp-web-qr', (qr) => {
        console.log('📱 QR Code recibido por Socket.io');
        setQrCode(qr);
        setStatus(prev => ({ ...prev, hasQR: true, qrCode: qr }));
      });
      
      // Escuchar nuevos mensajes
      socketRef.current.on('new-message', (message) => {
        if (message.source === 'whatsapp-web') {
          console.log('📨 Nuevo mensaje de WhatsApp Web recibido por Socket.io');
          // Si es del contacto seleccionado, agregar al chat
          if (selectedContact && message.phoneNumber === selectedContact.phoneNumber) {
            setMessages(prev => [...prev, message]);
          }
          // Actualizar solo el último mensaje del contacto sin recargar toda la lista
          setContacts(prev => {
            const updated = prev.map(c => {
              if (c.phoneNumber === message.phoneNumber) {
                return {
                  ...c,
                  lastMessage: message,
                  lastMessageTime: message.timestamp || message.createdAt
                };
              }
              return c;
            });
            // Si el contacto no existe, agregarlo
            if (!updated.some(c => c.phoneNumber === message.phoneNumber)) {
              const newContact = {
                phoneNumber: message.phoneNumber,
                name: message.name || message.phoneNumber,
                source: 'whatsapp-web',
                lastMessage: message,
                lastMessageTime: message.timestamp || message.createdAt
              };
              updated.push(newContact);
            }
            // Re-ordenar por último mensaje
            return updated.sort((a, b) => {
              const timeA = a.lastMessageTime ? new Date(a.lastMessageTime).getTime() : 0;
              const timeB = b.lastMessageTime ? new Date(b.lastMessageTime).getTime() : 0;
              return timeB - timeA;
            });
          });
        }
      });
      
      // Cargar estado inicial solo una vez
      loadStatus();
      loadContacts();
      
      // Actualizar solo el estado (no los contactos) cada 30 segundos
      const interval = setInterval(() => {
        loadStatus();
      }, 30000);
      
      return () => {
        if (socketRef.current) {
          socketRef.current.disconnect();
        }
        clearInterval(interval);
      };
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, selectedContact]);

  if (!isOpen) return null;

  return (
    <div className="whatsapp-web-panel-overlay">
      <div className="whatsapp-web-panel">
        <div className="whatsapp-web-header">
          <h2>📱 WhatsApp Web</h2>
          <button className="close-button" onClick={onClose}>✕</button>
        </div>

        {/* Estado de conexión */}
        <div className="whatsapp-web-status">
          {(() => {
            console.log('🔍 Renderizando estado:', { isReady: status.isReady, hasQR: status.hasQR, status });
            if (status.isReady === true) {
              return (
                <div className="status-indicator ready">
                  <span className="status-dot"></span>
                  Conectado
                </div>
              );
            } else if (status.hasQR === true) {
              return (
                <div className="status-indicator qr">
                  <span className="status-dot"></span>
                  Escanea el QR Code
                </div>
              );
            } else {
              return (
                <div className="status-indicator disconnected">
                  <span className="status-dot"></span>
                  Desconectado
                </div>
              );
            }
          })()}
        </div>

        {/* QR Code */}
        {qrCode && !status.isReady && (
          <div className="qr-code-container">
            <p>Escanee este código QR con WhatsApp:</p>
            <div className="qr-code">
              {/* Usar librería QR para renderizar */}
              <img 
                src={`https://api.qrserver.com/v1/create-qr-code/?size=200x200&data=${encodeURIComponent(qrCode)}`}
                alt="QR Code"
              />
            </div>
          </div>
        )}

        {/* Lista de conversaciones */}
        <div className="whatsapp-web-content">
          <div className="whatsapp-web-sidebar">
            <h3>Conversaciones</h3>
            {loading ? (
              <div className="loading">Cargando...</div>
            ) : contacts.length === 0 ? (
              <div className="empty-state">
                <p>No hay conversaciones de WhatsApp Web</p>
              </div>
            ) : (
              <div className="conversations-list">
                {contacts.map((contact) => (
                  <div
                    key={contact._id}
                    className={`conversation-item ${selectedContact?._id === contact._id ? 'active' : ''}`}
                    onClick={() => handleContactClick(contact)}
                  >
                    <div className="conversation-avatar">
                      {contact.name?.charAt(0)?.toUpperCase() || contact.phoneNumber.charAt(contact.phoneNumber.length - 1)}
                    </div>
                    <div className="conversation-info">
                      <div className="conversation-name">
                        {contact.name || contact.phoneNumber}
                      </div>
                      {contact.lastMessage && (
                        <div className="conversation-preview">
                          {contact.lastMessage.type === 'image' ? '📷 Imagen' :
                           contact.lastMessage.type === 'audio' ? '🎵 Audio' :
                           contact.lastMessage.content?.text?.substring(0, 30) || 'Mensaje'}
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Área de mensajes */}
          <div className="whatsapp-web-messages">
            {selectedContact ? (
              <>
                <div className="messages-header">
                  <div className="contact-info">
                    <div className="contact-avatar">
                      {selectedContact.name?.charAt(0)?.toUpperCase() || selectedContact.phoneNumber.charAt(selectedContact.phoneNumber.length - 1)}
                    </div>
                    <div>
                      <div className="contact-name">{selectedContact.name || selectedContact.phoneNumber}</div>
                      <div className="contact-phone">{selectedContact.phoneNumber}</div>
                    </div>
                  </div>
                </div>
                <div className="messages-list">
                  {messages.map((message) => (
                    <div key={message._id} className={`message-bubble ${message.direction === 'inbound' ? 'incoming' : 'outgoing'}`}>
                      <div className="message-content">
                        {message.type === 'text' && (
                          <div className="message-text">{message.content?.text}</div>
                        )}
                        {message.type === 'image' && (
                          (message.content?.mediaUrl || message.content?.localPath) ? (
                            <img 
                              src={message.content?.mediaUrl || 
                                   (message.content?.localPath ? `${API_URL}${message.content.localPath}` : '')}
                              alt="Imagen"
                              className="message-image"
                              onError={(e) => {
                                console.error('Error cargando imagen:', message.content?.localPath || message.content?.mediaUrl);
                                // Si falla con mediaUrl, intentar con localPath
                                if (message.content?.mediaUrl && message.content?.localPath && e.target.src.includes(message.content.mediaUrl)) {
                                  e.target.src = `${API_URL}${message.content.localPath}`;
                                }
                              }}
                            />
                          ) : (
                            <div className="message-placeholder">🖼️ Imagen (sin URL)</div>
                          )
                        )}
                        {message.type === 'audio' && (
                          (message.content?.mediaUrl || message.content?.localPath) ? (
                            <audio controls className="message-audio">
                              <source src={message.content?.mediaUrl || 
                                           (message.content?.localPath ? `${API_URL}${message.content.localPath}` : '')} />
                            </audio>
                          ) : (
                            <div className="message-placeholder">🎵 Audio (sin URL)</div>
                          )
                        )}
                        <div className="message-time">
                          {new Date(message.timestamp || message.createdAt).toLocaleTimeString('es-DO', { hour: '2-digit', minute: '2-digit' })}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </>
            ) : (
              <div className="empty-messages">
                <p>Selecciona una conversación para ver los mensajes</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default WhatsAppWebPanel;

